import { useDownloadCsv } from '@/api/carbon.api';
import Alert from '@/components/alert/alert.component';
import { SecondaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { cn } from '@/lib/utils';
import { IfPermitted, UserPermissions } from '@/modules/Permissions';
import { Download, TriangleAlert } from 'lucide-react';
import { toast } from 'react-toastify';

type Props = {
  projectId?: string;
  farmId?: string;
  template?: string;
};

export const triggerCsvDownload = ({
  farmId,
  projectId,
  template,
  mutate,
}: {
  farmId?: string;
  projectId?: string;
  template: string;
  mutate: ReturnType<typeof useDownloadCsv>['mutate'];
}) => {
  const toastId = toast.info('Downloading CSV...', { autoClose: false });

  mutate(
    { farmId, projectId, template },
    {
      onSuccess: (response) => {
        toast.dismiss(toastId);
        const blob = new Blob([response.data], { type: response.data.type });
        const url = window.URL.createObjectURL(blob);
        const cd = response.headers['content-disposition'] || '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'csv-sample-report.csv';

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Download complete');
      },
      onError: (error) => {
        toast.dismiss(toastId);
        toast.error(error.message || 'CSV download failed. Please try again later.');
      },
    }
  );
};

const DownloadCsv = ({ farmId, projectId, template }: Props) => {
  const downloadCsvMutation = useDownloadCsv();

  const triggerDownload = () => {
    triggerCsvDownload({ farmId, projectId, template: template || 'farm_csv_samples', mutate: downloadCsvMutation.mutate });
  };

  return (
    <IfPermitted permissions={[UserPermissions.DOWNLOAD_CSV_BUTTON]} fallback={null}>
      {downloadCsvMutation.status === 'idle' && (
        <Alert
          trigger={
            <SecondaryButton className="h-14">
              <div className={cn('flex items-center gap-1')}>
                <Download />
                CSV
              </div>
            </SecondaryButton>
          }
          title="Download report as CSV?"
          description="This will download the current report data as a CSV file."
          actionLabel="Download"
          onActionAccept={triggerDownload}
        />
      )}

      {downloadCsvMutation.status === 'pending' && (
        <div className="text-sm text-neutral-500 flex gap-2 items-center">
          <Loading size="small" inline />
          Downloading CSV...
        </div>
      )}

      {downloadCsvMutation.status === 'error' && (
        <div className="text-error-500 text-sm flex items-center gap-2">
          <TriangleAlert size={16} /> Download failed.
        </div>
      )}

      {downloadCsvMutation.status === 'success' && (
        <div className="text-sm text-green-600 flex items-center gap-2">
          <Download size={16} /> CSV Downloaded.
        </div>
      )}
    </IfPermitted>
  );
};

export default DownloadCsv;
